import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthContext";
import { api } from "@/lib/api";
import styles from "./HomePage.module.css";

/**
 * The public front door at `/`. Deliberately minimal — it does NOT expose links
 * to any content (that lives behind a space slug). A logged-in user is sent
 * straight to their own space; everyone else sees a welcome + Log in / Request
 * access.
 */
export function LandingPage() {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const [resolving, setResolving] = useState(false);

  // Once logged in, jump to the user's own space home.
  useEffect(() => {
    if (status !== "authenticated" || !user) return;
    let active = true;
    setResolving(true);
    void api.spaces.mine().then((res) => {
      if (!active) return;
      const first = res.success && res.data && res.data[0];
      if (first) navigate(`/${first.slug}`, { replace: true });
      else setResolving(false); // logged in but no space yet — stay on landing
    });
    return () => {
      active = false;
    };
  }, [status, user, navigate]);

  return (
    <div className="container">
      <section className={styles.hero}>
        <span className={styles.kicker}>AL Life Memory</span>
        <h1 className={styles.heroTitle}>
          Our Life, <span className={styles.heroAccent}>Remembered</span>
        </h1>
        <p className={styles.heroSubtitle}>
          A private home for life's moments — travels, food, pets, and the days
          in between. Access is by invitation.
        </p>

        {status === "anonymous" && (
          <div className={styles.landingActions}>
            <Link to="/login" className={styles.landingPrimary}>Log in</Link>
            <Link to="/register" className={styles.landingGhost}>Request access</Link>
            {/* The tools page needs no account — surface it to visitors who have
                no other reason to stay on this page. */}
            <Link to="/tools" className={styles.landingGhost}>🧰 Free tools</Link>
          </div>
        )}
        {(status === "loading" || resolving) && (
          <p className={styles.greeting} aria-busy="true">Loading…</p>
        )}
        {status === "authenticated" && user && !resolving && (
          <p className={styles.greeting}>
            You don't have a space yet — an admin will set one up for you. 💛
          </p>
        )}
      </section>
    </div>
  );
}
